import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, XCircle, AlertCircle, RotateCcw, ChevronRight, ChevronLeft, X, ZoomIn, ZoomOut, Maximize2, Pencil } from "lucide-react";
import DrawingBoard from "../../components/drawing/DrawingBoard";
import { ApiService } from "../../services/api";
import type { MakeupQuestion, MakeupSetResponse, ErrorType } from "../../types/navigator";

// Kişisel telafi / hazırbulunuşluk seti — öğrencinin kendi yanlışlarından ya da konu ölçümünden.
// Mevcut testler bir kez çözülebildiği için tekrar burada yapılır.
//
// EKRAN (24-25 Eyl): pop-up değil TAM SAYFA. Saha şikayeti: soru pop-up'ta 420 piksele sıkıştırılıyordu ve
// tyt-pipeline sorularında ŞIKLAR GÖRSELİN İÇİNDE olduğu için okunamıyordu. Görsel varsayılan olarak rahat
// okuma genişliğinde (READ_WIDTH) açılır — ekranı dolduran ilk sürüm "çok büyük" geldi — isteyen yakınlaştırır;
// üstüne çizim yapılabilir; şık düğmeleri alt şeritte sabit durur. Cevaplar sunucuda olduğu için set
// kapatılıp yeniden açılınca işaretlenen şıklar ve sonuçlar geri gelir, kaldığı sorudan devam eder.

const OPTION_LABELS = ["A", "B", "C", "D", "E"];
const READ_WIDTH = 720; // px — %100'ün karşılığı; telefonda ekran genişliğine iner
// Görseller %50'de açılır (sahibin tercihi, 25 Eyl): tyt-pipeline PNG'leri yüksek çözünürlüklü, %100'de
// yazı kitap puntosunun iki katı görünüyordu. Gösterge de %50 der; "0" bu boyuta döner.
const DEFAULT_ZOOM = 0.5;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const ERROR_LABEL: Record<ErrorType, { text: string; chip: string }> = {
  BILGI: { text: "bilmiyordun", chip: "border-rose-200 bg-rose-50 text-rose-700" },
  DIKKAT: { text: "dikkatsizlik", chip: "border-amber-200 bg-amber-50 text-amber-700" },
  SURE: { text: "süre yetmedi", chip: "border-sky-200 bg-sky-50 text-sky-700" },
};

interface Props {
  itemId?: string;
  setId?: string; // plansız set (hazırbulunuşluk kartı)
  onClose: () => void;
  onCompleted: () => void;
}

type Answered = { answer: string; isCorrect: boolean | null };

// Şık metni yalnız gerçekten metinse gösterilir; tyt-pipeline sorularında options ["A".."E"]
// yer tutucusudur ve asıl şıklar görselin içindedir.
const optionsOf = (q: MakeupQuestion): { label: string; text: string | null }[] => {
  const raw = q.question?.options;
  const list = Array.isArray(raw) && raw.length ? raw.map((o: any) => (typeof o === "string" ? o : o?.text ?? "")) : [];
  return OPTION_LABELS.map((label, i) => {
    const text = String(list[i] ?? "").trim();
    return { label, text: text && text.toUpperCase() !== label ? text : null };
  }).filter((_, i) => (list.length ? i < list.length : true));
};

export default function MakeupRunner({ itemId, setId, onClose, onCompleted }: Props) {
  const [data, setData] = useState<MakeupSetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [answered, setAnswered] = useState<Record<string, Answered>>({});
  const [checking, setChecking] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [summary, setSummary] = useState<{ percent: number; resolvedGains: number; rescheduled: number; konuOzet?: any; windowClosing?: boolean } | null>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [drawing, setDrawing] = useState(false);
  const [toolbarH, setToolbarH] = useState(0);
  const viewRef = useRef<HTMLDivElement | null>(null);
  const drawWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = setId ? await ApiService.getMakeupSet(setId) : await ApiService.openMakeupForItem(itemId as string);
        if (!alive) return;
        setData(res);
        // Daha önce verilen cevaplar sunucudan gelir: işaretler geri yüklenir, kaldığı sorudan devam edilir
        const restored: Record<string, Answered> = {};
        for (const q of res?.questions || []) {
          if (q.answeredAt) restored[q.id] = { answer: String(q.answer ?? ""), isCorrect: typeof q.isCorrect === "boolean" ? q.isCorrect : null };
        }
        setAnswered(restored);
        const firstOpen = (res?.questions || []).findIndex((q) => !q.answeredAt);
        setIdx(firstOpen >= 0 ? firstOpen : Math.max(0, (res?.questions || []).length - 1));
      } catch (e: any) {
        if (alive) setError(e?.message || "Set açılamadı");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [itemId, setId]);

  // Sayfa açıkken arkadaki liste kaymasın
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // NOT: "geri tuşu da kapatsın" denemesi KALDIRILDI. pushState + temizlikte history.back() çifti
  // StrictMode'un çift mount'unda paneli açılır açılmaz kapatıyordu (tarayıcıda ölçüldü: 24 Eyl).
  // Kapatma yolları: sağ üstteki X ve Esc. Gerçek rota istenirse ayrı iş.

  const questions = data?.questions || [];
  const current = questions[idx] || null;
  const done: Answered | undefined = current ? answered[current.id] : undefined;
  const verdict = done ? done.isCorrect : null;
  const allAnswered = questions.length > 0 && questions.every((q) => answered[q.id]);
  const isReadiness = data?.set?.kind === "READINESS" || data?.set?.kind === "SECTION_EXAM";

  const zoomBy = useCallback((delta: number) => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 100) / 100)));
  }, []);
  const fit = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
    if (viewRef.current) viewRef.current.scrollTo({ top: 0, left: 0 });
  }, []);

  // Soru değişince görünüm sıfırlanır — bir öncekinin yakınlaştırması yenisinde kalmasın.
  useEffect(() => {
    fit();
  }, [idx, fit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return drawing ? setDrawing(false) : onClose();
      if (e.key === "+" || e.key === "=") zoomBy(ZOOM_STEP);
      if (e.key === "-") zoomBy(-ZOOM_STEP);
      if (e.key === "0") fit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawing, onClose, zoomBy, fit]);

  // Kalem açıkken araç çubuğu sorunun ÜSTÜNE biniyordu (saha, 25 Eyl). Çubuğun yüksekliği ölçülür ve görsel
  // o kadar aşağıdan başlar; çubuk telefonda iki satıra sarsa da doğru kalır (ResizeObserver).
  // DrawingBoard'un yapısına dayanır: kök → ilk çocuk = araç çubuğu.
  useEffect(() => {
    if (!drawing) {
      setToolbarH(0);
      return;
    }
    const toolbar = drawWrapRef.current?.firstElementChild?.firstElementChild as HTMLElement | null;
    if (!toolbar) return;
    const update = () => setToolbarH(toolbar.offsetHeight + 16); // m-2 üst+alt boşluk
    update();
    const ro = new ResizeObserver(update);
    ro.observe(toolbar);
    return () => ro.disconnect();
  }, [drawing]);

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  };

  const answer = async (choice: string) => {
    if (!data?.set || !current || checking || done) return;
    setChecking(true);
    try {
      const res = await ApiService.answerMakeupQuestion(data.set.id, current.id, choice);
      setAnswered((prev) => ({ ...prev, [current.id]: { answer: choice, isCorrect: !!res.isCorrect } }));
    } catch (e: any) {
      setError(e?.message || "Cevap kaydedilemedi");
    } finally {
      setChecking(false);
    }
  };

  const finish = async () => {
    if (!data?.set) return;
    setFinishing(true);
    try {
      const res = await ApiService.completeMakeupSet(data.set.id);
      setSummary({ percent: res.percent ?? 0, resolvedGains: res.resolvedGains ?? 0, rescheduled: res.rescheduled ?? 0, konuOzet: res.konuOzet ?? null, windowClosing: !!res.windowClosing });
      onCompleted();
    } catch (e: any) {
      setError(e?.message || "Set tamamlanamadı");
    } finally {
      setFinishing(false);
    }
  };

  // Sıradaki: bu sorudan sonraki ilk CEVAPSIZ soru; yoksa baştaki cevapsız; hepsi bittiyse seti bitir
  const next = async () => {
    const after = questions.findIndex((q, i) => i > idx && !answered[q.id]);
    if (after >= 0) return setIdx(after);
    const any = questions.findIndex((q) => !answered[q.id]);
    if (any >= 0) return setIdx(any);
    await finish();
  };

  const title = data?.set?.title || (setId ? "Hazırbulunuşluk" : "Telafi seti");
  const solving = !loading && !error && !summary && data?.set && current;
  const answeredCount = questions.filter((q) => answered[q.id]).length;

  // Sayfa gövdeye portal ile basılır: Koçum düzeninde dönüşümlü (transform) bir üst öğe var ve `fixed`
  // onun kutusuna göre konumlanıyordu — katman 80 piksel aşağıdan başlayıp ŞIK ŞERİDİNİ ekranın altına
  // taşıyordu (tarayıcıda ölçüldü: altSerit.top 911 > ekran 900; şıklar tıklanamıyordu).
  return createPortal(
    <div data-coach-runner className="fixed inset-0 z-[9999] flex flex-col bg-gray-50">
      {/* Üst şerit: başlık, soru gezinme, ilerleme, araçlar */}
      <header className="shrink-0 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 px-3 py-2 sm:px-4">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
            {solving && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <button type="button" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} title="Önceki soru" className="grid h-6 w-6 place-items-center rounded hover:bg-gray-100 disabled:opacity-30">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="tabular-nums">
                  Soru {idx + 1} / {questions.length}
                </span>
                <button type="button" onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))} disabled={idx >= questions.length - 1} title="Sonraki soru" className="grid h-6 w-6 place-items-center rounded hover:bg-gray-100 disabled:opacity-30">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <span className="ml-1 hidden sm:inline">· {answeredCount} cevaplandı</span>
                {current?.errorType && <span className="ml-2 hidden sm:inline">· geçen sefer: {ERROR_LABEL[current.errorType].text}</span>}
              </div>
            )}
          </div>

          {solving && (
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => zoomBy(-ZOOM_STEP)} title="Küçült (−)" className="grid h-9 w-9 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-xs tabular-nums text-gray-500">%{Math.round(zoom * 100)}</span>
              <button type="button" onClick={() => zoomBy(ZOOM_STEP)} title="Büyüt (+)" className="grid h-9 w-9 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
                <ZoomIn className="h-4 w-4" />
              </button>
              <button type="button" onClick={fit} title="Başlangıç boyutu (0)" className="hidden h-9 w-9 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 sm:grid">
                <Maximize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setDrawing((d) => !d)}
                title="Soru üzerine çiz"
                className={`grid h-9 w-9 place-items-center rounded-lg border transition-colors ${drawing ? "border-blue-300 bg-blue-50 text-blue-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          )}

          <button type="button" onClick={onClose} title="Kapat" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        {solving && (
          <div className="h-1 w-full bg-gray-100">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
          </div>
        )}
      </header>

      {/* Orta alan: soru. Varsayılan okuma genişliği; yakınlaştırınca genişler ve kaydırılır. */}
      <main className="relative min-h-0 flex-1">
        {loading && (
          <div className="grid h-full place-items-center">
            <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        )}

        {!loading && error && (
          <div className="mx-auto mt-6 flex max-w-lg items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        {/* Çözülecek yanlış kalmamış — koç dürüstçe söylüyor */}
        {!loading && !error && data && !data.set && (
          <div className="mx-auto mt-6 max-w-lg rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
            <p className="text-sm font-medium text-gray-900">{data.message || "Tekrar çözülecek yanlış kalmamış."}</p>
            <button onClick={onClose} className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white">
              Kapat
            </button>
          </div>
        )}

        {summary && (
          <div className="mx-auto mt-6 max-w-lg rounded-lg border border-gray-200 bg-white p-6 text-center">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-blue-50 text-blue-600">
              <span className="text-lg font-bold">%{summary.percent}</span>
            </div>
            {isReadiness ? (
              <>
                <p className="text-base font-semibold text-gray-900">Hazırbulunuşluk tamamlandı</p>
                <p className="mt-1 text-sm text-gray-600">
                  {summary.konuOzet
                    ? `${summary.konuOzet.biliyor} konu biliyor · ${summary.konuOzet.yarim} yarım · ${summary.konuOzet.bilmiyor} bilmiyor` +
                      (summary.konuOzet.olculemeyen ? ` · ${summary.konuOzet.olculemeyen} ölçülemedi` : "")
                    : "Sonuç haritana işlendi."}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {summary.windowClosing
                    ? "Bütün dersler ölçüldü. Koçun şimdi bütün sonuçlara bakıp programını kuruyor ve sözleşme mektubunu yazıyor — Bugün'e dönünce programın hazır, mektup birkaç dakika içinde Mektuplar'da."
                    : summary.rescheduled > 0
                    ? `Bilmediklerin için koçun planına ${summary.rescheduled} çalışma eklendi.`
                    : "Bilmediklerin haritana işlendi. Ölçüm sürerken plan değişmez; program bütün dersler ölçülünce bu sonuçlardan kurulur."}
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-semibold text-gray-900">Telafi tamamlandı</p>
                <p className="mt-1 text-sm text-gray-600">
                  {summary.resolvedGains > 0 && `${summary.resolvedGains} kazanımın eksiği kapandı. `}
                  {summary.rescheduled > 0
                    ? `Takıldığın ${summary.rescheduled} kazanım için koçun yarına çalışma ekledi.`
                    : summary.resolvedGains === 0
                    ? "Bu sette kazanım kapanmadı — koçun seni izlemeye devam edecek."
                    : ""}
                </p>
              </>
            )}
            <button onClick={onClose} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">
              Tamam
            </button>
          </div>
        )}

        {solving && (
          <>
            <div ref={viewRef} onWheel={onWheel} className="h-full w-full overflow-auto overscroll-contain p-3 sm:p-4" style={toolbarH ? { paddingTop: toolbarH } : undefined}>
              {current!.question?.media?.url ? (
                <img
                  src={current!.question!.media!.url as string}
                  alt="Soru"
                  draggable={false}
                  onDoubleClick={() => (zoom === DEFAULT_ZOOM ? zoomBy(DEFAULT_ZOOM) : fit())}
                  className="mx-auto block select-none rounded bg-white shadow-sm"
                  // Genişlik: %100 = 720px, başlangıç %50 = 360px. Başlangıçta ekrandan geniş olmaz; büyütünce kaydırılır.
                  style={{ width: `${Math.round(READ_WIDTH * zoom)}px`, maxWidth: zoom <= DEFAULT_ZOOM ? "100%" : "none" }}
                />
              ) : (
                // Metin soru: görselin aksine küçültülmez — başlangıçta 720px sütun ve 15px yazı, büyütme orantılı
                <div className="mx-auto rounded-lg border border-gray-200 bg-white p-4" style={{ width: `${Math.round(READ_WIDTH * (zoom / DEFAULT_ZOOM))}px`, maxWidth: zoom <= DEFAULT_ZOOM ? "100%" : "none" }}>
                  <p className="whitespace-pre-wrap text-gray-800" style={{ fontSize: `${Math.round(15 * (zoom / DEFAULT_ZOOM))}px`, lineHeight: 1.6 }}>
                    {current!.question?.content || "Soru içeriği yok"}
                  </p>
                </div>
              )}
            </div>

            {/* Çizim katmanı sorunun ÜSTÜNE biner; kapalıyken tıklamalar görsele geçer. */}
            {drawing && (
              <div ref={drawWrapRef} className="absolute inset-0 z-20">
                <DrawingBoard className="h-full w-full" onClose={() => setDrawing(false)} />
              </div>
            )}
          </>
        )}
      </main>

      {/* Alt şerit: şıklar her zaman görünür (görsel büyürken kaybolmaz); cevaplanmışsa işaret kalır */}
      {solving && (
        <footer className="shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 sm:px-4">
          {current!.previousWrongAnswer !== null && current!.previousWrongAnswer !== undefined && !done && (
            <p className="mb-2 flex items-center gap-1.5 text-xs text-gray-500">
              <RotateCcw className="h-3.5 w-3.5" />
              Geçen sefer{" "}
              <b className="text-gray-700">
                {current!.previousWrongAnswer === "" ? "boş bırakmıştın" : `"${current!.previousWrongAnswer}" demiştin`}
              </b>
            </p>
          )}

          <div className="grid grid-cols-5 gap-2">
            {optionsOf(current!).map(({ label, text }) => {
              const marked = done?.answer === label;
              const tone = !done
                ? "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                : marked
                ? verdict
                  ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                  : "border-rose-400 bg-rose-50 text-rose-800"
                : "border-gray-200 bg-gray-50 text-gray-400";
              return (
                <button
                  key={label}
                  onClick={() => answer(label)}
                  disabled={checking || !!done}
                  aria-pressed={marked}
                  className={`flex min-h-[48px] flex-col items-center justify-center rounded-lg border px-1 py-2 text-sm font-semibold transition-colors disabled:cursor-default ${tone}`}>
                  <span>{label}</span>
                  {text && <span className="mt-0.5 line-clamp-2 text-[11px] font-normal opacity-80">{text}</span>}
                </button>
              );
            })}
          </div>

          {done && (
            <div
              className={`mt-2 flex items-center gap-2 rounded-lg border p-3 text-sm ${
                verdict ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"
              }`}>
              {verdict ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
              <span className="min-w-0 flex-1">
                {verdict ? "Doğru — bu sefer oldu." : isReadiness ? "Bu konu ölçüme işlendi." : "Bu sefer de olmadı. Koçun bu kazanımı tekrar planlayacak."}
              </span>
              <button
                onClick={next}
                disabled={finishing}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm disabled:opacity-60">
                {allAnswered ? (finishing ? "Bitiriliyor…" : "Bitir") : "Sıradaki"}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </footer>
      )}
    </div>,
    document.body
  );
}
